/**
 * FREE FUNNEL · Assessment page (replaces the paid-first Stripe flow).
 *
 * Three phases:
 *   Phase 0 — Intro: capture {first_name, email} → POST /free-start → lead_id
 *   Phase 1 — Questions: 6-step quiz (asset range, confidence, risk, LTC, priority, contact)
 *   Phase 2 — Submit: POST /free-submit → redirect to /wealth/results?lead_id=...
 *
 * NO Stripe. Toolkit upsell lives on the results page.
 */
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const ASSET_RANGES = [
    { v: "under_100k", label: "Less than $100,000" },
    { v: "100k_250k", label: "$100,000 – $250,000" },
    { v: "250k_500k", label: "$250,000 – $500,000" },
    { v: "500k_1m", label: "$500,000 – $1 million" },
    { v: "1m_2m", label: "$1 million – $2 million" },
    { v: "over_2m", label: "Over $2 million" },
];
const CONFIDENCE = ["Very confident", "Confident", "Somewhat confident", "Not confident", "No idea"];
const RISK = ["Not worried", "Slightly worried", "Worried", "Very worried"];
const LTC = [
    { v: "yes_funded", label: "Yes — I have a funded plan" },
    { v: "yes_partial", label: "Partially — some coverage, known gap" },
    { v: "no_concerned", label: "No, and I'm worried about it" },
    { v: "no_not_concerned", label: "No, and not concerned" },
    { v: "unsure", label: "I'm not sure" },
];
const PRIORITIES = [
    { v: "income", label: "Income I can't outlive" },
    { v: "growth", label: "Growing my savings" },
    { v: "protection", label: "Protecting what I have from market drops" },
    { v: "legacy", label: "Leaving a legacy" },
];

const QUESTIONS = [
    { key: "asset_range", title: "How much do you have saved for retirement (total)?", help: "All accounts combined — 401(k), IRA, brokerage, savings, real estate equity.", options: ASSET_RANGES },
    { key: "income_confidence", title: "How confident are you in your future retirement income?", help: "Be honest — gut feeling is fine.", options: CONFIDENCE.map((c) => ({ v: c.toLowerCase().replace(/[^a-z]/g, "_"), label: c })) },
    { key: "market_worry", title: "How worried are you about a market downturn affecting your retirement?", options: RISK.map((c) => ({ v: c.toLowerCase().replace(/[^a-z]/g, "_"), label: c })) },
    { key: "ltc_plan", title: "Do you have a plan for long-term care if you need it?", options: LTC },
    { key: "top_priority", title: "What matters most to you right now?", options: PRIORITIES },
];

const TOTAL = QUESTIONS.length;

export default function FreeAssessment() {
    const navigate = useNavigate();
    const [phase, setPhase] = useState("intro");      // intro | questions | submitting
    const [leadId, setLeadId] = useState(null);
    const [step, setStep] = useState(0);
    const [working, setWorking] = useState(false);
    const [intro, setIntro] = useState({ first_name: "", email: "" });
    const [quiz, setQuiz] = useState({});

    const progress = useMemo(() => Math.round(((step + 1) / TOTAL) * 100), [step]);

    const submitIntro = async (e) => {
        e?.preventDefault?.();
        if (!intro.first_name.trim() || !intro.email.trim()) {
            toast.error("Please enter your first name and email.");
            return;
        }
        setWorking(true);
        try {
            const r = await api.post("/wealth/free-start", { ...intro, origin_url: window.location.origin });
            setLeadId(r.data.lead_id);
            setPhase("questions");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setWorking(false);
        }
    };

    const choose = (key, value) => {
        const next = { ...quiz, [key]: value };
        setQuiz(next);
        if (step < TOTAL - 1) {
            setStep(step + 1);
        } else {
            submitQuiz(next);
        }
    };

    const submitQuiz = async (finalQuiz) => {
        setPhase("submitting");
        try {
            await api.post("/wealth/free-submit", { lead_id: leadId, quiz: finalQuiz });
            navigate(`/wealth/results?lead_id=${leadId}`);
        } catch (err) {
            toast.error(formatApiError(err));
            setPhase("questions");
        }
    };

    if (phase === "submitting") {
        return (
            <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf6]" data-testid="assessment-submitting">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-emerald-700 animate-spin mx-auto" />
                    <h2 className="mt-5 font-serif text-2xl text-emerald-950 font-semibold">Calculating your Retirement Readiness Score…</h2>
                    <p className="mt-2 text-emerald-900">Personalizing your scorecard. Just a moment.</p>
                </div>
            </div>
        );
    }

    if (phase === "intro") {
        return (
            <div className="min-h-[80vh] bg-[#fdfbf6] py-16">
                <div className="max-w-2xl mx-auto px-6">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold tracking-wide">
                            <Sparkles className="w-3.5 h-3.5" /> 100% FREE · NO CREDIT CARD
                        </div>
                        <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-emerald-950 font-semibold leading-tight">
                            Get your free Retirement Readiness Score
                        </h1>
                        <p className="mt-4 text-base text-emerald-900 leading-relaxed">
                            Five short questions. A clear 0-100 score with a personalized breakdown. Yours instantly — no payment required.
                        </p>
                    </div>
                    <form onSubmit={submitIntro} className="bg-white rounded-2xl border border-emerald-900/10 shadow-xl p-7" data-testid="assessment-intro-form">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-emerald-950 mb-1.5">First name</label>
                                <input
                                    type="text"
                                    value={intro.first_name}
                                    onChange={(e) => setIntro({ ...intro, first_name: e.target.value })}
                                    placeholder="What should we call you?"
                                    className="w-full px-4 py-3 rounded-lg border border-emerald-900/15 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-200 outline-none transition-colors text-emerald-950"
                                    autoComplete="given-name"
                                    data-testid="assessment-intro-firstname"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-emerald-950 mb-1.5">Email</label>
                                <input
                                    type="email"
                                    value={intro.email}
                                    onChange={(e) => setIntro({ ...intro, email: e.target.value })}
                                    placeholder="you@example.com"
                                    className="w-full px-4 py-3 rounded-lg border border-emerald-900/15 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-200 outline-none transition-colors text-emerald-950"
                                    autoComplete="email"
                                    data-testid="assessment-intro-email"
                                    required
                                />
                                <p className="mt-1.5 text-xs text-emerald-900">Where to send your score. No spam — just your results.</p>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={working}
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-emerald-900 hover:bg-emerald-800 text-amber-300 font-bold text-base disabled:opacity-60 transition-colors"
                            data-testid="assessment-intro-submit"
                        >
                            {working ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Start my free score <ArrowRight className="w-5 h-5" /></>}
                        </button>
                        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-emerald-900">
                            <ShieldCheck className="w-4 h-4 text-emerald-700" /> Encrypted · No credit card · Educational use only
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    // Questions phase
    const q = QUESTIONS[step];
    return (
        <div className="min-h-[80vh] bg-[#fdfbf6] py-12">
            <div className="max-w-2xl mx-auto px-6">
                {/* Progress */}
                <div className="mb-8">
                    <div className="flex justify-between text-xs text-emerald-900 font-semibold mb-2">
                        <span>Step {step + 1} of {TOTAL}</span>
                        <span>{progress}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-emerald-900/10 overflow-hidden">
                        <div className="h-full bg-emerald-700 transition-all duration-300" style={{ width: `${progress}%` }} />
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-emerald-900/10 shadow-xl p-7" data-testid={`assessment-step-${step}`}>
                    <h2 className="font-serif text-2xl text-emerald-950 font-semibold leading-snug">{q.title}</h2>
                    {q.help && <p className="mt-2 text-sm text-emerald-900">{q.help}</p>}
                    <div className="mt-6 space-y-2.5">
                        {q.options.map((opt) => (
                            <button
                                key={opt.v}
                                onClick={() => choose(q.key, opt.v)}
                                className="w-full text-left px-5 py-3.5 rounded-lg border border-emerald-900/15 bg-white hover:border-emerald-700 hover:bg-emerald-50 transition-all text-emerald-950 font-medium"
                                data-testid={`assessment-option-${q.key}-${opt.v}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {step > 0 && (
                    <button
                        onClick={() => setStep(step - 1)}
                        className="mt-5 inline-flex items-center gap-1.5 text-sm text-emerald-900 hover:text-emerald-800 font-semibold"
                        data-testid="assessment-back"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                )}
            </div>
        </div>
    );
}
